
import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger/logger.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IframeQueueService } from './iframe-queue.service';
import jwtDecode from 'jwt-decode';
import { CredentialManagerService } from './session/identifiers/credential-management.service';

const MILLISECONDS_IN_SECOND = 1000;

export interface AuthorizeConfigBase {
    client_id: string;
    nonce: string;
    redirect_uri: string;
    response_type: string;
    scope: string;
    realm: string;
    state: string;
    message_id?: string;
    ttl?: number;
    auth_id_guid?: string;
}

export interface PingConfig {
    auth_method: string;
    realm: string;
    auth_id_user_token?: string;
}

export type PingParams = Partial<AuthorizeConfigBase> & Partial<PingConfig>;

@Injectable()
export class OauthClientService {
    jwtDecodeService = jwtDecode;
    private readonly credentialManagerService = inject(CredentialManagerService);

    constructor(
        private iframeQueueService: IframeQueueService,
        private LOGGER: LoggerService
    ) { }

    authorize(params: PingParams, updateToken = false): Observable<any> {
        if (!environment.PRODUCTION && sessionStorage.getItem(environment.OAUTH_CONFIG.loginTokenSessionStorageLocation)) {
            params.auth_id_user_token = sessionStorage.getItem(environment.OAUTH_CONFIG.loginTokenSessionStorageLocation);
        }
        let endpoint = environment.OAUTH_CONFIG.oauthEndpoint + this.toQueryString(params);
        if (updateToken) {
            this.LOGGER.info('Calling IAM to renew ping token');
            endpoint = `${environment.RENEWAL_TOKEN_ENDPOINT}${window.location.origin}${environment.OAUTH_REDIRECT_HANDLER_PATH}&timestamp=${Date.now()}`;
        } else {
            this.LOGGER.info('Calling Oauth for access token');
        }
        this.iframeQueueService.nextIn(endpoint);
        return this.iframeQueueService.listenForIframeQueueOutEvent().pipe(
            map((authServerResponse) => this.mapResponse(authServerResponse, updateToken)),
            tap((authMappedResponse) => this.setAccessTokenToStorage(authMappedResponse))
        );
    }

    private generateTokenExpiration(expiresIn: number): number {
        return Date.now() + expiresIn * MILLISECONDS_IN_SECOND;
    }

    private mapResponse(authServerResponse: string | void, updateToken: boolean): any {
        if (authServerResponse) {
            this.iframeQueueService.clearQueueOut();
            const hashParams = authServerResponse.split('#')[1];
            let successful = false;

            let authParams = {};
            if (hashParams) {
                authParams = hashParams
                    .split('&')
                    .reduce((aggregatedParams, paramString) => {
                        const [key, value] = paramString.split('=');
                        aggregatedParams[key] = value;
                        return aggregatedParams;
                    }, {});
                successful = 'access_token' in authParams;
            } else if (typeof authServerResponse === 'string' && authServerResponse.includes('refreshResult=01')) {
                successful = true;
            }
            const authType = updateToken ? 'ping' : 'access';
            return { successful, authParams, authType };
        }
    }

    private setAccessTokenToStorage(authMappedResponse): void {
        if (!authMappedResponse?.successful || authMappedResponse.authType !== 'access') {
            return;
        }

        const authParams = authMappedResponse.authParams;
        sessionStorage.setItem('tokenDetails', `${authParams.access_token}, ${this.generateTokenExpiration(authParams.expires_in)}, ${authParams.expires_in}`);
        sessionStorage.setItem('access_token', authParams.access_token);
        sessionStorage.setItem('jsonWebToken', authParams.id_token);

        if (authParams.id_token) {
            this.storeDecodedToken(authParams.id_token);
        }
    }

    private storeDecodedToken(idToken: string): void {
        const decodedToken = <any>(this.jwtDecodeService(idToken));
        if (!decodedToken) {
            return;
        }

        const ecn = this.resolveEcn(decodedToken);
        if (ecn) {
            sessionStorage.setItem('ecn', ecn);
        }

        this.credentialManagerService.userInfo = this.toUserInfo(decodedToken, ecn);
        sessionStorage.setItem('UserName', decodedToken.userId);
        sessionStorage.setItem('guid', decodedToken.guid);
        sessionStorage.setItem('firstName', decodedToken.given_name);
        sessionStorage.setItem('email', decodedToken.email);
    }

    private toUserInfo(decodedToken: any, ecn: string): any {
        return {
            ...decodedToken,
            ecn,
            ...(decodedToken.given_name || decodedToken.firstName ? {
                given_name: decodedToken.given_name ?? decodedToken.firstName
            } : {}),
            ...(decodedToken.family_name || decodedToken.lastName ? {
                family_name: decodedToken.family_name ?? decodedToken.lastName
            } : {}),
            ...(decodedToken.email || decodedToken.emailAddress ? {
                email: decodedToken.email ?? decodedToken.emailAddress
            } : {})
        };
    }

    private toQueryString(authorizeParams: object): string {
        return `?${Object
            .keys(authorizeParams)
            .map((paramName) => `${paramName}=${authorizeParams[paramName]}`)
            .join('&')}`;
    }

    private resolveEcn(decodedToken: { sub?: string; ecn?: string }): string {
        const ecnFromSub = /^unidentified(?:\.(?:enterpriseCustomerNumber|ecn))?:(.+)$/.exec(decodedToken?.sub ?? '')?.[1];
        return ecnFromSub ?? decodedToken?.ecn ?? '';
    }
}
